import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useHistory } from 'react-router-dom';
import { TransferFundRequest } from '../types';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, Title, SubTitle, Button, Description } = vp;

type FC = FunctionComponent;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type dataProps = {
  TransactionID: string;
  DebitorClientAccount: string;
  RecipientClientAccount: string;
  DebitorClientAccountID: string;
  RecipientClientAccountID: string;
  TransferType: string;
  values: TransferFundRequest;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
};

const TransferDetails: FC = () => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const { state } = useLocation<dataProps>();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  useEffect(() => {
    if (state.TransactionID) {
      fetchTransactionsHistory({
        StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
        EndDateTime: moment().utc().format('YYYY-MM-DD'),
        TransactionID: state.TransactionID,
      });
    }
  }, [state.TransactionID]);

  return (
    <>
      <Box mb="md">
        <Title title="Transfer Overview" />
      </Box>

      <Box pt="md" pb="md">
        <SubTitle subTitle="Transfer" />
      </Box>
      <Description bold>Transaction Details</Description>
      <Table {...getTableProps('transactionDetails')} />
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: 'Transaction ID',
            value: (
              <Box gap="md" ai="center">
                {state.TransactionID}
                <Button
                  id="fund-transaction-id"
                  label="Details"
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setOpenModule('statements');
                    onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                    history.push({
                      pathname: `/transactions-history/details/${removeWhiteSpace(
                        transactions[0]?.TransactionType,
                      )}/${state.TransactionID}/${transactions[0]?.Currency}`,
                      state: { transactionId: state.TransactionID },
                    });
                  }}
                />
              </Box>
            ),
          },
          {
            display: 'Type',
            value: 'Account Transfer',
          },
          {
            display: 'Debitor Client Account',
            value: (
              <Box jc="fs" ai="center" gap="sm">
                <Box>{state.DebitorClientAccount || ''}</Box>
                <Button
                  id="view-client-account-details"
                  label="View"
                  width="50px"
                  height="30px"
                  color="secondary"
                  onClick={() => {
                    history.push(`/client-accounts/${state.DebitorClientAccountID}`);
                    onSubNavClick(
                      'clientAccountsList',
                      `/client-accounts/${state.DebitorClientAccountID}`,
                      'Client Accounts List',
                    );
                  }}
                />
              </Box>
            ),
          },
          {
            display: 'Recipient Client Account',
            value: (
              <Box jc="fs" ai="center" gap="sm">
                <Box>{state.RecipientClientAccount || ''}</Box>
                <Button
                  id="view-client-account-details"
                  label="View"
                  width="50px"
                  height="30px"
                  color="secondary"
                  onClick={() => {
                    history.push(`/client-accounts/${state.RecipientClientAccountID}`);
                    onSubNavClick(
                      'clientAccountsList',
                      `/client-accounts/${state.RecipientClientAccountID}`,
                      'Client Accounts List',
                    );
                  }}
                />
              </Box>
            ),
          },
          {
            display: 'Amount',
            value: `$${state.values.Amount}`,
          },
          {
            display: 'Currency',
            value: `${state.values.Currency}`,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Transaction Detail' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },
    };
    return handlers[handler]();
  }
  function removeWhiteSpace(TransactionType: string) {
    return decodeURI(TransactionType).replaceAll(' ', '');
  }
};

export default TransferDetails;
